import { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useSearchParams } from "react-router";
import { RoomType } from "@prisma/client";
import NavBar from "@app/components/NavBar";
import UserSummary from "@app/components/UserSummary";
import Onboarding from "@app/components/Onboarding";
import Star from "@app/icons/Star";
import useUser from "@app/zustand/user";
import { UserConnexionResponse } from "@api/src/types/responses";
import { refreshUser } from "@app/utils/use-most-fresh-user";
import getRoomName from "@app/utils/get-room-name";
import useMedspotStore from "@app/zustand/store";

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
    onNativePushToken: (token: string) => void;
    onAppVersion: (version: string) => void;
    onGetInsetBottomHeight: (insets: number) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onNotificationReceived: (notification: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onNotificationResponseReceived: (response: any) => void;
  }
}

export default function HomeLayout() {
  const me = useUser((state) => state.user!);
  const setUser = useUser((state) => state.setUser);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const isDrawerOpen = useMedspotStore((state) => state.drawerOpen);
  const setDrawerIsOpening = useMedspotStore(
    (state) => state.setDrawerIsOpening,
  );
  const setDrawerIsClosing = useMedspotStore(
    (state) => state.setDrawerIsClosing,
  );
  const location = useLocation();
  const isOnboardingDoneKey = "medspot-isOnboardingDone-202412";
  useEffect(() => {
    const isOnboardingDone = window.localStorage.getItem(isOnboardingDoneKey);
    if (!isOnboardingDone) {
      setIsOnboardingOpen(true);
    }
  }, []);

  useEffect(() => {
    window.onNativePushToken = function handleNativePushToken(token) {
      console.log("onNativePushToken", token);
      const form = new FormData();
      form.append("native-subscription", token);
      fetch(`${import.meta.env.VITE_API_URL}/user/${me.plausible_id}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          native_push_subscription: token,
        }),
      })
        .then((response) => response.json() as Promise<UserConnexionResponse>)
        .then((response) => {
          if (response.ok) {
            setUser(response.data.user);
          } else if (response.error) {
            alert(response.error);
          }
        });
    };
    setTimeout(() => {
      window.ReactNativeWebView?.postMessage(
        "request-native-expo-push-permission",
      );
    }, 250);
  }, [me.plausible_id, setUser]);

  const [bottomPadding, setBottomPadding] = useState(0);
  useEffect(() => {
    window.onGetInsetBottomHeight = (height: number) => {
      setBottomPadding(height);
    };
    window.ReactNativeWebView?.postMessage(
      "request-native-get-inset-bottom-height",
    );
  }, []);

  useEffect(() => {
    refreshUser("home-layout");
  }, [location.key]);

  const qFavs = searchParams.get("qFavs");
  const searchQuery = searchParams.get("search");
  const qRoomId = searchParams.get("qRoomId");
  const qRoomType = searchParams.get("qRoomType");
  useEffect(() => {
    // if (searchParams.has("reload")) {
    //   window.location.replace(window.location.href.replace(/&?reload=true/, ""));
    // }
    if (
      location.pathname.startsWith("/users") ||
      location.pathname.startsWith("/profile") ||
      location.pathname.startsWith("/stats")
    ) {
      return;
    }
    if (qFavs || searchQuery) {
      // do nothing
    } else if (qRoomId) {
      const lastRoom = me.rooms.find((r) => r.room.id === qRoomId);
      if (!lastRoom) {
        const mainRoom = me.rooms.find(
          (r) => r.room.roomType === RoomType.ETABLISSEMENT,
        );
        console.log("mainRoom", mainRoom);
        if (mainRoom) {
          searchParams.set("qRoomId", mainRoom.room.id);
          searchParams.set("qRoomType", mainRoom.room.roomType!);
          setSearchParams(searchParams);
        }
      }
    } else if (!qRoomId) {
      const lastRoomId = window.localStorage.getItem("medspot-lastRoomId");
      const lastRoom = me.rooms.find((r) => r.room.id === lastRoomId);
      if (lastRoom) {
        searchParams.set("qRoomId", lastRoom.room.id);
        searchParams.set("qRoomType", lastRoom.room.roomType!);
        setSearchParams(searchParams);
      } else {
        const mainRoom = me.rooms.find(
          (r) => r.room.roomType === RoomType.ETABLISSEMENT,
        );
        if (mainRoom) {
          searchParams.set("qRoomId", mainRoom.room.id);
          searchParams.set("qRoomType", mainRoom.room.roomType!);
          setSearchParams(searchParams);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.localStorage.setItem("medspot-lastRoomId", qRoomId as string);
    window.localStorage.setItem("medspot-lastRoomType", qRoomType as string);
  }, [qRoomId, qRoomType]);

  const channels = useMemo(() => {
    return me.rooms.filter((r) => r.room.roomType !== RoomType.DIRECT);
  }, [me.rooms]);
  const directMessages = useMemo(() => {
    return me.rooms
      .filter((r) => r.room.roomType === RoomType.DIRECT)
      .sort((a, b) => {
        return a.name && b.name ? a.name.localeCompare(b.name) : -1;
      });
  }, [me.rooms]);

  return (
    <div
      className={[
        "flex h-full w-full flex-col",
        "transform transition-transform duration-300 ease-in-out",
        isDrawerOpen ? "translate-x-full md:translate-x-0" : "translate-x-0",
      ].join(" ")}
      onTransitionEnd={() => {
        setDrawerIsOpening(false);
        setDrawerIsClosing(false);
      }}
      style={{ paddingBottom: bottomPadding }}
      key={searchParams.has("reload") ? "true" : "false"}
    >
      <NavBar />
      <div className="flex w-full flex-1 overflow-hidden bg-stone-100">
        <div className="mx-auto flex w-full flex-1 overflow-hidden px-1 pb-4 md:max-w-6xl md:px-2">
          <div className="scrollbar mr-8 mt-4 hidden h-full w-80 overflow-y-auto overflow-x-hidden scroll-smooth pb-8 md:block">
            <UserSummary user={me} />
            <hr className="my-1 border-none" />
            <aside
              className="drop-shadow-xs relative hidden h-min w-56 shrink-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white pb-1 pt-4 md:flex"
              id="profile"
            >
              <p className="px-4 text-xs font-semibold">Mes fils d'actualité</p>
              <ul className="mt-2 px-1.5">
                {channels.map((roomRelation) => {
                  const room = roomRelation.room;
                  const isCurrentRoom = searchParams.get("qRoomId") === room.id;
                  const newMessages =
                    room.lastMessageAt! > roomRelation.lastVisitAt!;
                  if (room.roomType === RoomType.SPECIALITE) return null;
                  if (room.roomType === RoomType.STATUS_PRO) return null;
                  if (room.roomType === RoomType.UNIVERSITAIRE) return null;
                  return (
                    <li key={room.id} className="w-full">
                      <Link
                        to={`/?qRoomType=${room.roomType}&qRoomId=${room.id}`}
                        className={[
                          "my-1 flex w-full items-center rounded-lg px-2 py-1 text-xs shadow-sm ring-1 ring-inset ring-gray-100",
                          isCurrentRoom
                            ? "bg-sky-800 text-white hover:bg-sky-600"
                            : "bg-white text-gray-900 hover:bg-gray-50",
                          newMessages ? "font-semibold" : "",
                        ].join(" ")}
                      >
                        <span className="mr-2 inline-flex size-8 shrink-0 items-center justify-center text-center text-2xl">
                          {room.roomType === RoomType.ETABLISSEMENT && "🏥"}
                          {/* {room.roomType === RoomType.SPECIALITE && "🧰"} */}
                          {/* {room.roomType === RoomType.STATUS_PRO && "🥼"} */}
                          {room.roomType ===
                            RoomType.SPECIALITE_AND_STATUS_PRO && "⚕️"}
                          {/* {room.roomType === RoomType.UNIVERSITAIRE && "🎓"} */}
                        </span>
                        <span>{getRoomName(roomRelation)}</span>
                        {newMessages && (
                          <span className="ml-auto size-2 rounded-full bg-sky-600 text-xs" />
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </aside>
            <hr className="my-1 border-none" />
            <aside
              className="drop-shadow-xs relative hidden h-min w-56 shrink-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white md:flex"
              id="profile"
            >
              <ul className="">
                <li className="w-full">
                  <Link
                    to={`/?qFavs=true`}
                    className={[
                      "flex w-full items-center rounded-lg px-2 py-1 text-xs font-semibold shadow-sm ring-1 ring-inset ring-gray-100",
                      searchParams.get("qFavs") === "true"
                        ? "bg-sky-800 text-white hover:bg-sky-600"
                        : "bg-white text-gray-900 hover:bg-gray-50",
                    ].join(" ")}
                  >
                    <span className="mr-2 inline-flex size-8 shrink-0 items-center justify-center text-center text-2xl">
                      <Star filled className="pointer-events-none size-5" />
                    </span>
                    <span>Mes posts favoris</span>
                  </Link>
                </li>
              </ul>
            </aside>
            <hr className="my-1 border-none" />
            <aside
              className="drop-shadow-xs relative hidden h-min w-56 shrink-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white md:flex"
              id="profile"
            >
              <ul className="">
                <li className="w-full">
                  <Link
                    to={`/?search=`}
                    className={[
                      "flex w-full items-center rounded-lg px-2 py-1 text-xs font-semibold shadow-sm ring-1 ring-inset ring-gray-100",
                      searchParams.has("search")
                        ? "bg-sky-800 text-white hover:bg-sky-600"
                        : "bg-white text-gray-900 hover:bg-gray-50",
                    ].join(" ")}
                  >
                    <span className="mr-2 inline-flex size-8 shrink-0 items-center justify-center text-center text-2xl">
                      🔎
                    </span>
                    <span>Rechercher un post</span>
                  </Link>
                </li>
              </ul>
            </aside>
            <hr className="my-1 border-none" />

            <aside
              className="drop-shadow-xs relative hidden h-min w-56 shrink-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white pb-1 pt-4 md:flex"
              id="profile"
            >
              <div className="items-left flex flex-col justify-between gap-y-2">
                <p className="ml-4 text-xs font-semibold">
                  Mes groupes de discussion
                </p>
                <Link
                  to={{
                    pathname: "/users",
                    search: `qRoomType=${RoomType.DIRECT}`,
                  }}
                  className="ml-4 text-xs font-semibold text-sky-900"
                >
                  + Démarrer une discussion
                </Link>
              </div>
              <ul className="mt-2 px-1.5">
                {directMessages.map((roomRelation) => {
                  return (
                    <li key={`${roomRelation.updatedAt}`} className="w-full">
                      <Link
                        to={`/?qRoomType=${roomRelation.room.roomType}&qRoomId=${encodeURIComponent(
                          roomRelation.room.id,
                        )}`}
                        className={[
                          "my-1 flex min-h-8 w-full items-center rounded-lg px-2 py-1 text-xs shadow-sm ring-1 ring-inset ring-gray-100",
                          searchParams.get("qRoomId") === roomRelation.room.id
                            ? "bg-sky-800 text-white hover:bg-sky-600"
                            : "bg-white text-gray-900 hover:bg-gray-50",
                        ].join(" ")}
                      >
                        {roomRelation?.avatar ? (
                          <span className="mr-2 flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-sky-900 bg-white text-sky-900">
                            <img
                              className="h-full w-full object-cover"
                              src={roomRelation.avatar}
                              loading="lazy"
                              alt={`Avatar de ${roomRelation.name}`}
                            />
                          </span>
                        ) : (
                          <span className="mr-2 inline-flex shrink-0 items-center justify-center text-left">
                            👥
                          </span>
                        )}
                        <span>
                          {roomRelation.room?.manualName ??
                            roomRelation.name
                              ?.split("__")
                              .join(", ")
                              .split("_")
                              .join(" ")}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </aside>
          </div>
          <Outlet />
          <div className="hidden flex-col gap-2 md:flex">
            {/* hashtags gone - whatelse ? */}
          </div>
        </div>
      </div>
      <Onboarding
        open={isOnboardingOpen}
        onClose={() => {
          setIsOnboardingOpen(false);
          window.localStorage.setItem(isOnboardingDoneKey, "true");
        }}
      />
    </div>
  );
}
